import React, { useRef, useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { heroContent } from "../mock";
import { Star } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { HighlightText } from "../components/ui/animated-reveal-text";
import SocialDock from "./SocialDock";
import AudioPlayerToast from "./AudioPlayerToast";
import { useModeContext } from "../contexts/ModeContext";
import { Squares } from "./ui/squares-background";
import { GeometricBackground } from "./ui/geometric-background";

gsap.registerPlugin(useGSAP);

const clamp = (min, val, max) => Math.min(Math.max(val, min), max);

const Hero = () => {
  const { activeMode } = useModeContext();
  const [prevMode, setPrevMode] = useState(activeMode);
  const root = useRef(null);
  const imageRef = useRef(null);
  const playRef = useRef(null);
  const ringRef = useRef(null);
  const titleTopRef = useRef(null);
  const titleBottomRef = useRef(null);
  const [isAudioPlayerOpen, setIsAudioPlayerOpen] = useState(false);
  
  // Smooth mode change animation
  useEffect(() => {
    if (prevMode !== activeMode) {
      // Animate title change
      const titleElements = [titleTopRef.current, titleBottomRef.current];
      titleElements.forEach(el => {
        if (el) {
          gsap.fromTo(el, 
            { y: 10, opacity: 0.7 }, 
            { y: 0, opacity: 1, duration: 0.6, ease: "power2.out", delay: 0.3 }
          );
        }
      });
      
      // Animate buttons with stagger
      const buttons = root.current?.querySelectorAll('button, .hero-rating');
      if (buttons) {
        gsap.fromTo(buttons, 
          { scale: 0.95, opacity: 0.8 }, 
          { scale: 1, opacity: 1, duration: 0.4, ease: "back.out(1.7)", stagger: 0.1, delay: 0.4 }
        );
      }
      
      setPrevMode(activeMode);
    }
  }, [activeMode, prevMode]);

  // Dynamic title content based on mode
  const titleContent = activeMode === 0 
    ? { 
        topEn: "BEHAVIOUR", topDe: "VERHALTENS", bottomEn: "CHANGE", bottomDe: "ÄNDERUNG",
        // Standard sizes for Behaviour Change
        topMobileSize: "text-[clamp(2rem,11vw,4.2rem)]", topDesktopSize: "md:text-[clamp(2.4rem,11vw,9rem)]",
        topEnMobileSize: "text-[clamp(2.6rem,13.5vw,5rem)]", topEnDesktopSize: "md:text-[clamp(2.8rem,13vw,10.5rem)]",
        topDeMobileSize: "text-[clamp(2rem,11vw,4.2rem)]", topDeDesktopSize: "md:text-[clamp(2.4rem,11vw,9rem)]",
        bottomMobileSize: "text-[clamp(2rem,11vw,4.2rem)]", bottomDesktopSize: "md:text-[clamp(2.4rem,11vw,9rem)]",
        bottomEnMobileSize: "text-[clamp(2.6rem,13.5vw,5rem)]", bottomEnDesktopSize: "md:text-[clamp(2.8rem,13vw,10.5rem)]",
        bottomDeMobileSize: "text-[clamp(2rem,11vw,4.2rem)]", bottomDeDesktopSize: "md:text-[clamp(2.4rem,11vw,9rem)]"
      }
    : { 
        topEn: "PEAK", topDe: "SPITZEN-", bottomEn: "PERFORMANCE", bottomDe: "LEISTUNG",
        // Smaller sizes for Peak Performance (longer text), but LEISTUNG gets bigger size
        topMobileSize: "text-[clamp(2rem,11vw,4.2rem)]", topDesktopSize: "md:text-[clamp(2.4rem,11vw,9rem)]",
        topEnMobileSize: "text-[clamp(2.6rem,13.5vw,5rem)]", topEnDesktopSize: "md:text-[clamp(2.8rem,13vw,10.5rem)]",
        topDeMobileSize: "text-[clamp(2rem,11vw,4.2rem)]", topDeDesktopSize: "md:text-[clamp(2.4rem,11vw,9rem)]",
        bottomMobileSize: "text-[clamp(1.6rem,9vw,3.5rem)]", bottomDesktopSize: "md:text-[clamp(2rem,9vw,7.5rem)]",
        bottomEnMobileSize: "text-[clamp(1.8rem,10.5vw,4rem)]", bottomEnDesktopSize: "md:text-[clamp(2.2rem,10vw,8rem)]",
        bottomDeMobileSize: "text-[clamp(2.2rem,12vw,4.5rem)]", bottomDeDesktopSize: "md:text-[clamp(2.6rem,12vw,8.5rem)]" // LEISTUNG larger
      };

  useGSAP(
    () => {
      const ctx = gsap.context(() => {
        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
        tl.from(".title-line", { y: 50, opacity: 0, duration: 0.9, stagger: 0.1 })
          .from(".hero-highlight", { width: 0, opacity: 0, duration: 0.7 }, "<0.2")
          .from(".hero-paragraph", { y: 24, opacity: 0, duration: 0.6 }, "-0.2")
          .from(".hero-rating", { scale: 0.8, opacity: 0, duration: 0.4 }, "-0.2")
          .from(imageRef.current, { x: 80, opacity: 0, duration: 0.9 }, "-0.2");

        gsap.to(playRef.current, { scale: 1.06, repeat: -1, yoyo: true, ease: "sine.inOut", duration: 1.4 });
        gsap.to(ringRef.current, { rotate: 360, duration: 16, repeat: -1, ease: "none", transformOrigin: "50% 50%" });
      }, root);
      return () => ctx.revert();
    },
    { scope: root }
  );

  const onMouseMove = (e) => {
    if (!imageRef.current) return;
    const rect = root.current.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const relY = (e.clientY - rect.top) / rect.height;
    const tx = clamp(-12, (relX - 0.5) * 24, 12);
    const ty = clamp(-8, (relY - 0.5) * 16, 8);
    gsap.to(imageRef.current, { x: tx, y: ty, duration: 0.4, ease: "sine.out" });
  };

  const c = heroContent.colors;

  // GSAP Titel-Translation – Desktop: Hover, Mobile: Tap-Toggle (stabilisiert)
  useGSAP(() => {
    const container = root.current?.querySelector('h1');
    if (!container) return;

    // Nur auf Geräten mit Maus/feinem Pointer aktivieren (Touch-Geräte überspringen)
    const canHover = typeof window !== 'undefined' && window.matchMedia('(hover:hover) and (pointer:fine)').matches;

    const top = titleTopRef.current;
    const bottom = titleBottomRef.current;
    if (!top || !bottom) return;

    const topEn = top.querySelector('.en-text');
    const topDe = top.querySelector('.de-text');
    const bottomEn = bottom.querySelector('.en-text');
    const bottomDe = bottom.querySelector('.de-text');
    if (!topEn || !topDe || !bottomEn || !bottomDe) return;

    // Ausgangszustand definieren
    gsap.set([topEn, bottomEn], { y: '0%' });
    gsap.set([topDe, bottomDe], { y: '100%' });

    let activeTl = null;

    if (canHover) {
      const onEnter = () => {
        activeTl?.kill();
        activeTl = gsap.timeline({ defaults: { duration: 0.4, ease: 'power2.out' }, overwrite: true })
          .to([topEn, bottomEn], { y: '-100%' }, 0)
          .to([topDe, bottomDe], { y: '0%' }, 0.05);
      };

      const onLeave = () => {
        activeTl?.kill();
        activeTl = gsap.timeline({ defaults: { duration: 0.4, ease: 'power2.out' }, overwrite: true })
          .to([topDe, bottomDe], { y: '100%' }, 0)
          .to([topEn, bottomEn], { y: '0%' }, 0.05);
      };

      container.addEventListener('mouseenter', onEnter);
      container.addEventListener('mouseleave', onLeave);

      return () => {
        container.removeEventListener('mouseenter', onEnter);
        container.removeEventListener('mouseleave', onLeave);
        activeTl?.kill();
      };
    } else {
      let isGerman = false;

      const onToggle = () => {
        activeTl?.kill();
        if (!isGerman) {
          activeTl = gsap.timeline({ defaults: { duration: 0.35, ease: 'power2.out' }, overwrite: true })
            .to([topEn, bottomEn], { y: '-100%' }, 0)
            .to([topDe, bottomDe], { y: '0%' }, 0.04);
        } else {
          activeTl = gsap.timeline({ defaults: { duration: 0.35, ease: 'power2.out' }, overwrite: true })
            .to([topDe, bottomDe], { y: '100%' }, 0)
            .to([topEn, bottomEn], { y: '0%' }, 0.04);
        }
        isGerman = !isGerman;
      };

      container.addEventListener('click', onToggle);

    return () => {
        container.removeEventListener('click', onToggle);
        activeTl?.kill();
    };
    }
  }, { scope: root });

  return (
    <section
      id="hero"
      ref={root}
      onMouseMove={onMouseMove}
      className="relative w-full min-h-[100svh] pt-[120px] md:pt-[80px] overflow-hidden"
    >
      {/* Background - conditional based on mode */}
      {activeMode === 0 ? (
      <div className="absolute inset-0 -z-10" style={{ background: "linear-gradient(180deg, rgba(215,239,231,0.22) 0%, rgba(255,255,255,0) 40%)" }} />
      ) : (
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <GeometricBackground />
        </div>
      )}

      {/* Social dock bottom-right (desktop only; erscheint erst ab der 2. Sektion) */}
      <div className="hidden md:block fixed right-6 bottom-6 z-40">
        <SocialDock showAfterSelector="#kompetenzen" hideInSelector={["#hero", "footer"]} />
      </div>

      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-12 -mt-4 md:-mt-8">
          <h1 className="leading-[0.9] font-semibold tracking-[-0.04em] group cursor-pointer text-right md:text-left relative z-20 pr-3 md:pr-0" style={{ color: activeMode === 1 ? 'rgb(229 229 229 / var(--tw-text-opacity, 1))' : c.heading }}>
            <span 
              ref={titleTopRef}
              className={`block title-line ${titleContent.topMobileSize} ${titleContent.topDesktopSize} relative`}
              style={{ overflowY: 'hidden', overflowX: 'visible', lineHeight: '1.1', paddingTop: '0.1em', paddingBottom: '0.1em' }}
              data-en={titleContent.topEn}
              data-de={titleContent.topDe}
            >
              <span className={`en-text transition-transform duration-500 ease-out block ${titleContent.topEnMobileSize} ${titleContent.topEnDesktopSize}`} style={{ lineHeight: '1.1' }}>{titleContent.topEn}</span>
              <span className={`de-text transition-transform duration-500 ease-out block absolute inset-0 transform translate-y-full ${titleContent.topDeMobileSize} ${titleContent.topDeDesktopSize}`} style={{ lineHeight: '1.1' }}>{titleContent.topDe}</span>
            </span>
            <span 
              ref={titleBottomRef}
              className={`block title-line ${titleContent.bottomMobileSize} ${titleContent.bottomDesktopSize} relative`}
              style={{ overflowY: 'hidden', overflowX: 'visible', lineHeight: '1.1', paddingTop: '0.1em', paddingBottom: '0.1em' }}
              data-en={titleContent.bottomEn}
              data-de={titleContent.bottomDe}
            >
              <span className={`en-text transition-transform duration-500 ease-out block ${titleContent.bottomEnMobileSize} ${titleContent.bottomEnDesktopSize}`} style={{ lineHeight: '1.1' }}>{titleContent.bottomEn}</span>
              <span className={`de-text transition-transform duration-500 ease-out block absolute inset-0 transform translate-y-full ${titleContent.bottomDeMobileSize} ${titleContent.bottomDeDesktopSize}`} style={{ lineHeight: '1.1' }}>{titleContent.bottomDe}</span>
            </span>
          </h1>

          <div className="mt-6 hero-highlight md:text-left text-right md:ml-0 ml-auto w-fit md:w-fit pr-3 md:pr-0 relative z-[5] md:z-20">
            <div className="hidden md:block">
            <HighlightText
              as="p"
              text={activeMode === 0 ? heroContent.bc.highlight : heroContent.pp.highlight}
                textClassName={`md:text-[25px] font-[600] drop-shadow-sm max-w-none ${activeMode === 0 ? 'text-white' : 'text-[#161616]'}`}
              highlightClassName={`rounded-md ${activeMode === 1 ? 'shine-loop' : ''}`}
              highlightColor="var(--brand-accent)"
              shineOnHover={true}
              shineColor="rgba(255,255,255,0.55)"
              duration={1.2}
              ease="easeInOut"
              style={{ fontFamily: "'Caveat', cursive" }}
              organic
            />
            </div>
            <p className={`md:hidden inline-block rounded-md px-2 py-0.5 text-[clamp(14px,4.6vw,18px)] font-[600] whitespace-nowrap ${activeMode === 0 ? 'bg-[#F59E0B] text-white' : 'bg-[var(--brand-accent)] text-[#161616]'} ${activeMode === 1 ? 'shine-loop' : ''}`} style={{ fontFamily: "'Caveat', cursive" }}>
              {activeMode === 0 ? "nachhaltig und menschlich." : "mit Fokus und Leichtigkeit."}
            </p>
          </div>

          <div className="hero-paragraph mt-6 max-w-[90%] md:max-w-2xl md:bg-transparent md:shadow-none md:border-0 md:p-0 md:ml-0 ml-auto relative z-20">
            <div className="md:hidden relative bg-white/95 border border-neutral-200 shadow-md rounded-2xl px-4 py-2.5 text-left text-neutral-800 backdrop-blur-sm ml-auto max-w-[70%] w-fit">
              <span className="absolute" style={{ left: "-10px", top: "12px", width: 0, height: 0, borderTop: "10px solid transparent", borderBottom: "10px solid transparent", borderRight: "10px solid rgba(255,255,255,0.95)" }} aria-hidden="true" />
              <span className="absolute" style={{ left: "-12px", top: "10px", width: 0, height: 0, borderTop: "12px solid transparent", borderBottom: "12px solid transparent", borderRight: "12px solid rgb(229,231,235)" }} aria-hidden="true" />
              <p className="text-[clamp(12px,3.4vw,15px)] leading-[1.6]">
                {activeMode === 0 
                  ? "Im Bereich Behavior Change ermögliche ich die Transformation alter Muster und die Etablierung neuer, wirksamer Gewohnheiten."
                  : "Im Bereich Executive Coaching & Peak Performance unterstütze ich dabei, beruflich wie privat auf das nächste Level zu kommen."
                }
              </p>
            </div>
            <p className={`hidden md:block text-[17px] md:text-[18px] leading-[1.6] ${activeMode === 1 ? 'text-neutral-200' : 'text-neutral-700'}`}>
            {activeMode === 0 ? heroContent.bc.paragraph : heroContent.pp.paragraph}
          </p>
          </div>

          <div className="mt-6 hidden md:flex flex-wrap items-center gap-4">
              <div className={`hero-rating flex items-center gap-2 rounded-md border ${activeMode === 1 ? 'border-white/20 bg-black/25' : 'border-white/60 bg-white/70'} backdrop-blur-sm px-3 py-2 ${activeMode === 1 ? 'text-white/90' : 'text-neutral-800'} shadow-sm`}>
                <span className="text-sm whitespace-nowrap">{activeMode === 1 ? "Transformation auf höchstem Niveau" : "Coaching mit messbarem Erfolg"}</span>
              <div className="flex items-center gap-1 ml-2">
                {Array.from({ length: heroContent.rating.outOf }).map((_, i) => (
                  <Star key={i} size={16} className={i < Math.round(heroContent.rating.value) ? "text-yellow-500 fill-yellow-500" : "text-neutral-300"} aria-hidden="true" />
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4 hidden md:flex items-center gap-3">
            {heroContent.ctas.map((cta) => (
              <a key={cta.label} href={cta.to} className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full">
                                  <Button
                    variant={cta.variant === "secondary" ? "secondary" : "default"}
                    className={`rounded-full h-11 px-6 text-[15px]
                      ${cta.variant === "secondary" 
                        ? activeMode === 1 
                          ? "bg-neutral-200 hover:bg-neutral-100 text-neutral-900 dark:bg-neutral-200 dark:hover:bg-neutral-100 dark:text-neutral-900" 
                          : "bg-[#A9BBB5] hover:bg-[#8FA19D] text-white"
                        : activeMode === 1
                          ? "bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-[#161616] dark:bg-[var(--brand-accent)] dark:hover:bg-[var(--brand-accent)]/90 dark:text-[#161616] shine-loop"
                          : ""
                      }
                    `}
                >
                  {cta.label}
                </Button>
              </a>
            ))}
          </div>

          {/* Mobile: Glass card mit Sternen und CTAs über Tom */}
          <div className={`md:hidden absolute z-30 transform ${activeMode === 1 ? 'left-1/2 -translate-x-1/2 top-[74%]' : 'right-4 top-[72%]'}`}>
            <div className={`backdrop-blur-md ${activeMode === 1 ? 'bg-black/25 border-white/20' : 'bg-white/35 border-white/50'} shadow-lg rounded-2xl flex flex-col gap-2 ${activeMode === 1 ? 'px-4 py-3 items-center min-w-[280px]' : 'px-3 py-2.5 items-end'}` }>
              <div className={`flex items-center gap-2 ${activeMode === 1 ? 'text-white/90' : 'text-neutral-900/90'}`}>
                <span className="text-xs whitespace-nowrap">{activeMode === 1 ? "Transformation auf höchstem Niveau" : "Coaching mit messbarem Erfolg"}</span>
                <div className="flex items-center gap-1 ml-1">
                  {Array.from({ length: heroContent.rating.outOf }).map((_, i) => (
                    <Star key={i} size={14} className={i < Math.round(heroContent.rating.value) ? "text-yellow-500 fill-yellow-500" : "text-neutral-300"} aria-hidden="true" />
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {heroContent.ctas.map((cta) => (
                  <a key={cta.label} href={cta.to} className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full">
                    <Button
                      variant={cta.variant === "secondary" ? "secondary" : "default"}
                      className={`rounded-full h-9 px-4 text-[13px]
                        ${cta.variant === "secondary" 
                          ? activeMode === 1 
                            ? "bg-neutral-200 hover:bg-neutral-100 text-neutral-900 dark:bg-neutral-200 dark:hover:bg-neutral-100 dark:text-neutral-900" 
                            : "bg-[#A9BBB5] hover:bg-[#8FA19D] text-white"
                          : activeMode === 1
                            ? "bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-[#161616] dark:bg-[var(--brand-accent)] dark:hover:bg-[var(--brand-accent)]/90 dark:text-[#161616] shine-loop"
                            : ""
                        }
                      `}
                    >
                      {cta.label}
                    </Button>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute right-28 md:right-6 top-1/2 transform -translate-y-1/2 z-10 md:z-30">
          <div className="relative max-w-[520px] transform translate-y-28 md:translate-y-16">
            <picture>
              <source media="(max-width: 767px)" srcSet={activeMode === 0 ? "/tombc.webp" : "/tom_mobil.webp"} />
              <img
                ref={imageRef}
                src={activeMode === 0 ? "/tombc.webp" : "/tom_desktop.webp"}
                alt={activeMode === 0 
                  ? "Tom Oppenhäuser - Behaviour Change Coach. Portrait eines professionellen Coaches in einem modernen Setting."
                  : "Tom Oppenhäuser - Executive & Peak Performance Coach. Portrait eines professionellen Coaches in einem modernen Setting."
                }
                className="relative z-10 w-full h-auto object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.12)] select-none"
                style={{ filter: "saturate(1.02)" }}
              />
            </picture>
            <button 
              ref={playRef} 
              onClick={() => setIsAudioPlayerOpen(true)}
              className={`absolute -right-24 md:-right-4 bottom-0 md:bottom-16 z-20 w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/95 border shadow-md ${activeMode === 1 ? 'hidden' : 'grid'} place-items-center hover:shadow-lg transition-shadow backdrop-blur-md`} 
              aria-label="Podcast abspielen"
            >
              <svg ref={ringRef} width="100%" height="100%" viewBox="0 0 96 96" className="absolute inset-0 pointer-events-none" aria-hidden="true">
                <defs>
                  <path id="textcircle" d="M48,48 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                </defs>
                <text fill="#9CA3AF" fontSize="9" fontWeight="500" letterSpacing="0.5">
                  <textPath href="#textcircle" startOffset="0" method="align" spacing="auto" textLength="238">
                    Podcast • Podcast • Podcast • Podcast • Podcast • Podcast •
                  </textPath>
                </text>
              </svg>
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-neutral-900 grid place-items-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 5v14l11-7L8 5z" fill="white" />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Audio Player Toast */}
      <AudioPlayerToast 
        isOpen={isAudioPlayerOpen} 
        onClose={() => setIsAudioPlayerOpen(false)} 
      />
    </section>
  );
};

export default Hero;